import { Injectable, Injector } from '@angular/core';

import {
  IQuantityOnHandRequestParams,
  IQuantityOnHandItem,
  IQuantityOnHandResponse,
  IBaseEntityResponse,
} from '@shared/types/entities';
import { MAX_PAGE_SIZE } from '@config/index';
import { Observable, rx } from '@shared/rx';
import { BaseService } from '../base/base.service';
import { ceil, chunk, concat, merge, range } from 'lodash';
import { HttpParams } from '@angular/common/http';
import { IFilterQuery } from '@shared/types';
import { batchRequestMap } from '@shared/rx-operators';

@Injectable()
export class QohService extends BaseService {
  public constructor(public injector: Injector) {
    super(injector);
  }

  public fetchQuantityOnHand(
    data: IQuantityOnHandItem[],
    userQuery?: IFilterQuery,
  ): Observable<IQuantityOnHandResponse> {
    const url = '/quantity-on-hand/search';
    const requestData: IQuantityOnHandRequestParams = {
      items: data,
    };
    const query: IQuantityOnHandRequestParams = merge({}, requestData);
    const params: HttpParams = this.createParams(userQuery);

    return this.http
      .put<IQuantityOnHandResponse>(url, query, { params })
      .pipe(this.catchHttpError());
  }

  public batchFetchQuantityOnHand(
    data: IQuantityOnHandItem[],
    userQuery?: IFilterQuery,
  ): Observable<IQuantityOnHandResponse> {
    const defaultPageSize = MAX_PAGE_SIZE;
    const defaultRequestQohChunkSize = 10;
    const buildPageRange = (total: number): number[] => {
      return range(1, ceil(total / defaultPageSize));
    };

    return batchRequestMap(
      chunk(data, defaultRequestQohChunkSize),
      (chunkData: IQuantityOnHandItem[]) => {
        const buildParams = (
          page: number,
        ): [IQuantityOnHandItem[], IFilterQuery] => [
          chunkData,
          merge(
            {},
            {
              top: defaultPageSize,
              skip: page * defaultPageSize,
              select: [
                'count',
                'entity_id',
                'site_id',
                'inventory_item_entity_id',
              ],
            },
            userQuery,
          ),
        ];

        return this.batchFetchDataHandler(
          this.fetchQuantityOnHand,
          buildParams,
          buildPageRange,
        );
      },
    ).pipe(
      rx.reduce(
        (
          preResponse: IBaseEntityResponse<any>,
          curResponse: IBaseEntityResponse<any>,
        ) => {
          return {
            ...curResponse,
            items: concat(preResponse.items, curResponse.items),
            total: preResponse.total + curResponse.total,
          };
        },
        {
          current_page: 0,
          items: [],
          page_count: 0,
          page_size: 0,
          total: 0,
        },
      ),
    );
  }
}
